// The "look before it sends" checkpoint for every email a button sends.
//
// Every frontend sender (emailSender.js) hands its draft to reviewEmailDraft() before posting.
// When the review popup (components/email/EmailReviewHost.jsx) is on screen for a signed-in
// user, the draft waits here until the user clicks Send (possibly after tweaking the subject or
// wording) or Cancel. With no popup mounted (tests, the client portal, the kiosk, public pages)
// or a draft marked `reviewed: true` (screens that already showed the full email, background
// sends), the draft goes straight through untouched.
//
// Batching: one button often sends several emails at once (seller + agent + team). Drafts that
// arrive within 350 ms of each other open ONE popup with a tab each, and a draft that arrives
// while the popup is still up joins it. Once the user clicks Send or Cancel the batch is done
// and the next draft starts a new one.
//
// Shaped for React's useSyncExternalStore: subscribeEmailReview + getEmailReviewSnapshot.
// The snapshot is null (nothing to review) or { id, items: [{ id, draft }] } and is a new
// object every time it changes.

import { withSenderBcc } from "./bccMe";

export const EMAIL_REVIEW_DEBOUNCE_MS = 350;

// Fields the popup may change. Recipients, type and everything else stay as the sender built them.
// A draft with `lockBody: true` (wording set and saved on the screen before, like the open house
// recap) may only have its subject changed.
const EDITABLE_FIELDS = ["subject", "text", "html"];
const LOCKED_BODY_EDITABLE_FIELDS = ["subject"];

let hostCount = 0;
let batch = null; // { id, items: [{ id, draft, resolve }], open: boolean, timer }
let snapshot = null;
let nextId = 1;
const listeners = new Set();

function publish() {
  snapshot =
    batch && batch.open
      ? { id: batch.id, items: batch.items.map(({ id, draft }) => ({ id, draft })) }
      : null;
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a broken listener must not block the others */
    }
  });
}

function openBatch() {
  if (!batch) return;
  batch.timer = null;
  batch.open = true;
  publish();
}

function finish(resolveItem) {
  if (!batch) return;
  const done = batch;
  batch = null;
  if (done.timer) clearTimeout(done.timer);
  done.items.forEach(resolveItem);
  publish();
}

/** True while the review popup is on screen for a signed-in user. */
export function isEmailReviewHostMounted() {
  return hostCount > 0;
}

/**
 * Hand a draft to the review popup.
 * @param {{to:string,toName?:string,cc?:string,bcc?:string,subject:string,text?:string,
 *          html?:string,type?:string,label?:string,reviewNote?:string,reviewed?:boolean,
 *          requireReview?:boolean,lockBody?:boolean,lockBodyNote?:string}} draft
 * @returns {Promise<object|null>} the (possibly edited) draft, or null when not to be sent.
 *
 * `requireReview`: a draft that may start while the screen is changing (the Timeline tab's held
 * client update flushes as the tab is left) is refused outright when no popup is mounted, the
 * same as a Cancel. It is never allowed through unseen.
 */
export function reviewEmailDraft(draft) {
  if (draft && draft.requireReview === true && draft.reviewed !== true && hostCount === 0) {
    return Promise.resolve(null);
  }
  if (!draft || draft.reviewed === true || hostCount === 0) {
    return Promise.resolve(draft);
  }
  return new Promise((resolve) => {
    const item = { id: `d${nextId++}`, draft, resolve };
    if (!batch) {
      batch = { id: `b${nextId++}`, items: [item], open: false, timer: null };
    } else {
      batch.items.push(item);
    }
    if (batch.open) {
      publish();
    } else {
      if (batch.timer) clearTimeout(batch.timer);
      batch.timer = setTimeout(openBatch, EMAIL_REVIEW_DEBOUNCE_MS);
    }
  });
}

/**
 * The popup announces itself. Returns the function that takes it away again. While at least one
 * host is registered, drafts wait for review. When the last one goes (sign-out), anything still
 * waiting is cancelled: an email nobody saw is never sent on its own.
 */
export function registerEmailReviewHost() {
  hostCount += 1;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    hostCount = Math.max(0, hostCount - 1);
    if (hostCount === 0) cancelEmailReview();
  };
}

export function subscribeEmailReview(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getEmailReviewSnapshot() {
  return snapshot;
}

/**
 * Send: resolves every draft in the open batch.
 * @param {Object<string, object|null>} editsById  item id -> { subject?, text?, html?, bccSelf? } edits
 *   (bccSelf: the sender's own address, added to Bcc by the BCC Me box),
 *   or null for a draft the user chose not to send. Items missing from the map go as they were.
 */
export function resolveEmailReview(editsById = {}) {
  finish(({ id, draft, resolve }) => {
    const edits = Object.prototype.hasOwnProperty.call(editsById, id) ? editsById[id] : undefined;
    if (edits === null) return resolve(null);
    const out = { ...draft };
    if (edits && typeof edits === "object") {
      (draft.lockBody ? LOCKED_BODY_EDITABLE_FIELDS : EDITABLE_FIELDS).forEach((f) => {
        if (edits[f] !== undefined) out[f] = edits[f];
      });
      // BCC Me (09/28/2026): the popup names the sender's OWN address, never a list. Recipients
      // are otherwise not editable here, and a client never moves off the visible To.
      if (typeof edits.bccSelf === "string" && edits.bccSelf) {
        out.bcc = withSenderBcc(draft, edits.bccSelf, true);
      }
    }
    resolve(out);
  });
}

/** Cancel: nothing in the batch is sent. */
export function cancelEmailReview() {
  finish(({ resolve }) => resolve(null));
}

const NAMED_ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  mdash: "-", ndash: "-", hellip: "...", rsquo: "'", lsquo: "'", rdquo: '"', ldquo: '"',
  copy: "(c)", reg: "(R)", trade: "(TM)", bull: "*", middot: "*",
};

function decodeEntities(s) {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, code) => {
    if (code[0] === "#") {
      const n = code[1] === "x" || code[1] === "X" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      if (!Number.isFinite(n)) return m;
      if (n === 160) return " ";
      try {
        return String.fromCodePoint(n);
      } catch {
        return m;
      }
    }
    const named = NAMED_ENTITIES[code.toLowerCase()];
    return named === undefined ? m : named;
  });
}

// A link as plain text: "Label (address)", or just the address when the label is the address or
// there is no label. A link with no address, or one to a spot on the same page (#...), keeps its
// words only. The address stays entity-encoded here; emailHtmlToText decodes the whole text once.
function linkToText(match, attrs, inner) {
  const m = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs || "");
  const href = m ? (m[1] ?? m[2] ?? m[3] ?? "").trim() : "";
  if (!href || href.startsWith("#")) return inner;
  const label = decodeEntities(String(inner).replace(/<[^>]+>/g, "")).replace(/\s+/g, " ").trim();
  if (!label || label === decodeEntities(href)) return href;
  return `${inner} (${href})`;
}

/**
 * Plain-text copy of an (edited) email's html, for the text fallback. Drops the <head>, styles
 * and scripts, keeps line breaks where blocks, rows and list items end, and keeps every link's
 * address (Approve, Decline, the feedback form, the portal) next to its words.
 */
export function emailHtmlToText(html) {
  let s = String(html ?? "");
  if (!s) return "";
  s = s.replace(/<!--[\s\S]*?-->/g, "");
  s = s.replace(/<head[\s\S]*?<\/head>/gi, "");
  s = s.replace(/<(style|script|title)[\s\S]*?<\/\1>/gi, "");
  s = s.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, linkToText);
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<\/(p|div|tr|li|h[1-6]|table|ul|ol|blockquote)>/gi, "\n");
  s = s.replace(/<\/t[dh]>/gi, " ");
  s = s.replace(/<[^>]+>/g, "");
  s = decodeEntities(s);
  s = s.replace(/\r\n?/g, "\n");
  s = s.replace(/[ \t\f\v]+/g, " ");
  s = s.replace(/ *\n */g, "\n");
  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

/**
 * The email's html with only what sits inside <body> swapped for `newInner`, so the head (the
 * email's styles) and the body tag's own attributes come through untouched. A fragment with no
 * <body> tag is replaced whole.
 */
export function replaceBodyInner(originalHtml, newInner) {
  const html = String(originalHtml ?? "");
  const inner = String(newInner ?? "");
  const open = /<body\b[^>]*>/i.exec(html);
  if (!open) return inner;
  const start = open.index + open[0].length;
  const close = html.toLowerCase().lastIndexOf("</body>");
  const end = close >= start ? close : html.length;
  return html.slice(0, start) + inner + html.slice(end);
}

// Tests only: forget every batch, host and listener.
export function __resetEmailReviewGateForTests() {
  if (batch && batch.timer) clearTimeout(batch.timer);
  batch = null;
  snapshot = null;
  hostCount = 0;
  listeners.clear();
}
